import { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { PillTabs } from "@/shared/components/PillTabs";
import { EmptyState } from "@/shared/components/EmptyState";
import { cn } from "@/shared/lib/utils";
import type { Medication } from "@/shared/types";
import type { ReviewItem } from "@/portals/patient/api/reviewItemsApi";
import { ANSWER_LABELS, itemConversation, toMedications } from "@/features/patient/medications/components/ReviewItemView";
import { PatientConversation } from "@/features/patient/medications/components/PatientConversation";
import { RxcuiTag } from "@/features/patient/medications/components/RxcuiTag";
import type { ReviewProcessing } from "@/portals/patient/api/reviewItemsApi";
import { ReviewProcessingBanner } from "./ReviewProcessingBanner";
import { ISSUE_LABELS, SourceBadge } from "@/features/patient/medications/components/medicationReviewBadges";
import { isPairItem, reviewTitle } from "@/features/patient/medications/components/ReviewConflictCard";
import { ConflictOptions } from "@/features/empi/ConflictOptions";
import { IDENTITY_REASON_TEXT, MATCH_BAND_TEXT, RESOURCE_NOUN, RESOURCE_TITLE, conflictReasonText, optionName } from "@/features/empi/empiPresentation";
import { outcomeActionLabel } from "@/features/patient/medications/components/reviewOutcomeLabels";
import { listCardClass } from "@/shared/lib/cardStyles";
import { ComparisonTable } from "@/shared/components/ComparisonTable";
import { ACTION_LABELS } from "@/features/patient/human-review/reviewActionLabels";

export { ACTION_LABELS };

/**
 * Human Review Queue — the patient's medication review items (Module 2/3),
 * in the HRQ's own design. Every item waiting on the patient is a Flagged
 * Record; the patient either reviews it (the review modal: answer + comment)
 * or skips it, and either way it goes on to their clinician / the shared
 * queue. Items they answered are Reviewed; items they skipped — or that moved
 * on by themselves because no answer came in time — are Skipped.
 */

interface HumanReviewQueueSectionProps {
  items: ReviewItem[];
  /** Opens the review modal for this item. */
  onReview: (itemId: string) => void;
  /** Sends the item to the clinician without an answer or comment. */
  onSkip: (itemId: string) => void;
  /** Item whose skip is in flight. */
  skippingId?: string | null;
  /** Opens with this item selected, on its tab (a notification link's ?item=). */
  focusItemId?: string | null;
  /** New records still being read / checked — shows the progress banner. */
  processing?: ReviewProcessing | null;
  /** The work just finished: show every step done and the outcome for a moment. */
  justFinished?: boolean;
  /** Opens the bulk-review screen (every open item, one after another). */
  /** Opens the bulk screen for the Flagged (waiting) or Skipped items. */
  onBulkReview?: (tab: "flagged" | "skipped") => void;
  /** How many items wait on the patient: the bulk-review card shows only when there are any. */
  openCount?: number;
}

type TabKey = "flagged" | "reviewed" | "skipped";

/** How many of the latest answered/skipped items Recently Reviewed shows. */
const RECENT_LIMIT = 15;

function lastResponse(item: ReviewItem) {
  return item.patientResponses[item.patientResponses.length - 1];
}

/** An identity check confirmed by the patient's own "Yes" on a record with exactly the same details. */
export function autoConfirmedText(item: ReviewItem): string | null {
  const identity = item.empi?.identity;

  if (identity?.matchMethod === "patient_other_name") {
    return "Added to your records automatically: it shows one of the names you confirmed as yours and your date of birth.";
  }

  if (identity?.matchMethod !== "patient_confirmed_pattern") return null;

  return identity.confirmedLikeLabel
    ? `Added to your records automatically: it shows exactly the same details you confirmed on “${identity.confirmedLikeLabel}”.`
    : "Added to your records automatically: it shows exactly the same details as a record you confirmed.";
}

/** A Skipped item the patient can still answer (bulk review of the Skipped tab). */
export function isAnswerableSkipped(item: ReviewItem): boolean {
  return hrqTab(item) === "skipped" && Boolean(item.canRespond);
}

export function hrqTab(item: ReviewItem): TabKey {
  if (item.awaitingInput) return "flagged";
  if (autoConfirmedText(item)) return "reviewed";
  const last = lastResponse(item);
  return last && last.answer !== "SKIPPED" ? "reviewed" : "skipped";
}

/**
 * How many items the queue shows as waiting on the patient — the Flagged tab
 * plus "Confirm your uploads". The dashboard's "Needs Attention" uses this,
 * so the two can never disagree.
 */
export function countNeedsAttention(items: ReviewItem[]): number {
  return items.filter((item) => hrqTab(item) === "flagged").length;
}

function daysSince(iso: string): number {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 0;
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

function formatDaysAgo(days: number): string {
  if (days <= 0) return "Today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
}

function formatDate(dateString: string): string {
  if (!dateString) return "Not available";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const date = new Date(dateString + "T00:00:00");
  if (Number.isNaN(date.getTime())) return "Not available";
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

/** The medicine the item is about: the newer record of a pair, else the one record. */
function subject(meds: Medication[]): Medication | undefined {
  return meds.find((m) => m.review?.recordRole === "INCOMING") ?? meds[0];
}

/** Where an item stands once it left the patient's hands. */
function outcomeTag(item: ReviewItem): { label: string; className: string } {
  if (item.itemStatus === "RESOLVED_CLINICIAN_VERIFIED") return { label: "Decided", className: "bg-[#DCFCE7] text-[#16A34A]" };
  if (item.itemStatus === "RESOLVED_PATIENT_REPORTED") return { label: "Closed", className: "bg-[#DCFCE7] text-[#16A34A]" };
  return { label: "With clinician", className: "bg-[#EEF2F7] text-[#0A2947]" };
}

/** "You answered: …" — or, for a plain reply to a question, that you replied. */
function answeredText(answer: ReviewItem["patientResponses"][number]["answer"]): string {
  if (answer === "REPLY") return "You replied to your clinician";
  if (answer === "USE_THIS_VALUE") return "You picked the value you think is right";
  return `You answered: ${ANSWER_LABELS[answer]}`;
}

function skippedText(item: ReviewItem): string {
  return lastResponse(item)
    ? "You skipped this, so your clinician decides without your input."
    : "No answer came in time, so it moved to your clinician automatically.";
}

interface Row {
  item: ReviewItem;
  meds: Medication[];
  /** The medicine a medication item is about; null on an EMPI item. */
  med: Medication | null;
  /** What the row is called: the medicine, or the EMPI item's subject. */
  title: string;
  /** "Medication", "Identity check", "Condition"… */
  kind: string;
  tab: TabKey;
}

/** Title and kind of an IDENTITY_MISMATCH / GOLDEN_RECORD_CONFLICT item. */
function empiRow(item: ReviewItem): { title: string; kind: string } | null {
  if (item.empi?.identity) {
    return { title: item.empi.identity.uploadLabel ? `Record from “${item.empi.identity.uploadLabel}”` : "A record from a recent upload", kind: "Identity check" };
  }
  if (item.empi?.conflict) {
    const { resourceType, options } = item.empi.conflict;
    return { title: optionName(resourceType, options[0]?.fields ?? {}), kind: RESOURCE_TITLE[resourceType] };
  }
  return null;
}

export function HumanReviewQueueSection({
  items, onReview, onSkip, skippingId, focusItemId, processing, justFinished, onBulkReview, openCount = 0,
}: HumanReviewQueueSectionProps) {
  const busy = Boolean(processing && processing.state !== "idle");
  const [activeTab, setActiveTab] = useState<TabKey>("flagged");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // A notification link (?item=) opens that item: switch to its tab and select
  // it — once per tab it lands on, so fresh data that moves it (e.g. the list
  // first shown from the page cache was older) still opens it in the right place.
  const focusedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusItemId) return;
    const target = items.find((i) => i.id === focusItemId);
    if (!target) return;
    // An identity check waiting on the patient: select it in "Confirm your
    // uploads" (top of Flagged Records) and open its answer form straight away.
    if (isPendingIdentityCheck(target)) {
      if (focusedRef.current === `${focusItemId}:identity`) return;
      focusedRef.current = `${focusItemId}:identity`;
      setActiveTab("flagged");
      setSelectedId(focusItemId);
      onReview(focusItemId);
      return;
    }
    const tab = hrqTab(target);
    if (focusedRef.current === `${focusItemId}:${tab}`) return;
    focusedRef.current = `${focusItemId}:${tab}`;
    setActiveTab(tab);
    setSelectedId(focusItemId);
  }, [focusItemId, items, onReview]);

  const rows = useMemo<Row[]>(
    () =>
      items
        // EMPI items carry their detail in `empi` (an identity check has no records at all).
        .filter((item) => item.records.length > 0 || item.empi)
        .flatMap((item): Row[] => {
          const empi = empiRow(item);
          if (empi) return [{ item, meds: [], med: null, ...empi, tab: hrqTab(item) }];
          const meds = toMedications(item);
          const med = subject(meds);
          // Nothing left to show (e.g. its upload was deleted and erased): left out, never a crash.
          if (!med) return [];
          return [{ item, meds, med, title: med.name, kind: "Medication", tab: hrqTab(item) }];
        }),
    [items],
  );

  // Flagged Records = everything waiting on the patient, in two groups:
  // patient-only identity checks ("Confirm your uploads") pinned first, then
  // the records to review.
  const identityChecks = rows.filter((r) => isPendingIdentityCheck(r.item));
  const toReview = rows.filter((r) => r.tab === "flagged" && !isPendingIdentityCheck(r.item));
  const flagged = [...identityChecks, ...toReview];
  const reviewed = rows.filter((r) => r.tab === "reviewed");
  const skipped = rows.filter((r) => r.tab === "skipped");
  const skippedOpenCount = skipped.filter((r) => isAnswerableSkipped(r.item)).length;
  const listForTab = activeTab === "flagged" ? flagged : activeTab === "reviewed" ? reviewed : skipped;
  // The first record of the tab is selected unless the patient picked (or closed) one.
  const selected =
    selectedId === "" ? null : listForTab.find((r) => r.item.id === selectedId) ?? listForTab[0] ?? null;

  // The 15 most recent the patient dealt with (answered, skipped, or moved on unanswered).
  const recent = [...reviewed, ...skipped]
    .map((r) => ({ ...r, at: lastResponse(r.item)?.respondedAt ?? r.item.createdAt }))
    .sort((a, b) => b.at.localeCompare(a.at))
    .slice(0, RECENT_LIMIT);

  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    setSelectedId(null);
  };

  const tabs: { key: TabKey; label: string; count: number }[] = [
    { key: "flagged", label: "Flagged Records", count: flagged.length },
    { key: "reviewed", label: "Reviewed", count: reviewed.length },
    { key: "skipped", label: "Skipped", count: skipped.length },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {justFinished ? (
        <ReviewProcessingBanner processing={processing} finished={{ needsReview: flagged.length }} />
      ) : (
        processing && <ReviewProcessingBanner processing={processing} />
      )}

      {/* The platform's pill splitter (same as the clinician dashboard / Document Panel). */}
      <div className="mb-5 flex justify-center">
        <PillTabs
          label="Review queue view"
          active={activeTab}
          onChange={handleTabChange}
          tabs={tabs.map((tab) => ({ id: tab.key, label: `${tab.label} (${tab.count})` }))}
        />
      </div>

      {activeTab === "flagged" && onBulkReview && openCount > 0 && (
        <BulkReviewCard count={openCount} onOpen={() => onBulkReview("flagged")} />
      )}
      {activeTab === "skipped" && onBulkReview && skippedOpenCount > 0 && (
        <BulkReviewCard count={skippedOpenCount} onOpen={() => onBulkReview("skipped")} skipped />
      )}

      {/* Side by side only when there's room next to the sidebar (xl); stacked on iPad.
          An empty tab is one full-width card instead of an empty list + empty detail. */}
      <div className={cn("grid items-start gap-5", listForTab.length > 0 && "xl:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]")}>
        {/* Left — record list */}
        <div className="grid gap-5">
          {listForTab.length === 0 ? (
            <EmptyState
              icon={<Icon icon={activeTab === "flagged" ? "lucide:clipboard-check" : activeTab === "reviewed" ? "lucide:check-circle-2" : "lucide:skip-forward"} className="h-8 w-8 text-[#9CA3AF]" />}
              title={
                activeTab === "flagged"
                  ? busy ? "Nothing to review yet" : "You're all caught up"
                  : activeTab === "reviewed" ? "No reviewed records yet" : "No skipped records yet"
              }
              description={
                activeTab === "flagged"
                  ? busy ? "We're still checking your new records." : "Records that need your answer will show up here."
                  : activeTab === "reviewed" ? "Records you've answered will show up here." : "Records you skip will show up here."
              }
              className="min-h-[300px] rounded-2xl"
            />
          ) : activeTab === "flagged" && identityChecks.length > 0 ? (
            <>
              <section aria-label={IDENTITY_SECTION_COPY.title} data-testid="hrq-identity-section" className="grid gap-3">
                <GroupHeading title={IDENTITY_SECTION_COPY.title} count={identityChecks.length} hint={IDENTITY_SECTION_COPY.hint} />
                {identityChecks.map((row) => (
                  <QueueRow key={row.item.id} row={row} selected={row.item.id === selected?.item.id} onSelect={setSelectedId} />
                ))}
              </section>
              {toReview.length > 0 && (
                <section aria-label="Records to review" className="grid gap-3">
                  <GroupHeading title="Records to review" count={toReview.length} />
                  {toReview.map((row) => (
                    <QueueRow key={row.item.id} row={row} selected={row.item.id === selected?.item.id} onSelect={setSelectedId} />
                  ))}
                </section>
              )}
            </>
          ) : (
            listForTab.map((row) => (
              <QueueRow key={row.item.id} row={row} selected={row.item.id === selected?.item.id} onSelect={setSelectedId} />
            ))
          )}
        </div>

        {/* Right — detail + recently reviewed. Sticky so it stays in view while
            scrolling a long left-hand list. */}
        <div className="sticky top-5">
          {selected ? (
            <DetailCard
              row={selected}
              onClose={() => setSelectedId("")}
              onReview={onReview}
              onSkip={onSkip}
              skipping={skippingId === selected.item.id}
            />
          ) : listForTab.length > 0 && (
            <div className="flex min-h-[300px] items-center justify-center rounded-2xl border border-[#E5E7EB] bg-white p-8 text-center text-sm text-[#6B7280]">
              Select a record from the list to review it.
            </div>
          )}

          {recent.length > 0 && (
            <section aria-label="Recently Reviewed" className="mt-5 rounded-2xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
              <h3 className="mb-3 text-sm font-extrabold text-[#0A2947]">
                Recently Reviewed <span className="font-semibold text-[#9CA3AF]">({recent.length})</span>
              </h3>
              {/* About three rows visible; the rest scroll inside the card. */}
              <div data-testid="hrq-recent-list" className="-mr-2 grid max-h-[252px] gap-3 overflow-y-auto pr-2">
                {recent.map(({ item, title, kind, tab, at }) => {
                  const answered = tab === "reviewed";
                  const tag = answered ? outcomeTag(item) : { label: "Skipped", className: "bg-[#F3F4F6] text-[#6B7280]" };
                  const last = lastResponse(item);
                  return (
                    <div key={item.id} data-testid="hrq-recent" className="flex items-center gap-3 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3">
                      <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", answered ? "bg-[#DCFCE7]" : "bg-[#F3F4F6]")}>
                        <Icon
                          icon={answered ? "lucide:check" : "lucide:skip-forward"}
                          className={cn("h-5 w-5", answered ? "text-[#16A34A]" : "text-[#6B7280]")}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-[#9CA3AF]">{kind}</p>
                        <p className="truncate text-sm font-bold text-[#0A2947]">{title}</p>
                        <p className="truncate text-xs text-[#6B7280]">
                          {autoConfirmedText(item) ?? (answered && last ? answeredText(last.answer) : skippedText(item))}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <span className={cn("inline-block rounded-full px-3 py-1 text-xs font-semibold", tag.className)}>{tag.label}</span>
                        <p className="mt-1 text-[11px] text-[#9CA3AF]">{formatDaysAgo(daysSince(at))}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

/** Top of Flagged Records: answer every open item on one screen instead of one by one. */
export function BulkReviewCard({ count, onOpen, skipped = false }: { count: number; onOpen: () => void; skipped?: boolean }) {
  return (
    <section
      aria-label="Bulk review"
      data-testid="bulk-review-card"
      className="mb-5 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[#E5E7EB] bg-white p-5 shadow-sm"
    >
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#E4002B]/50">
          <Icon icon="lucide:list-checks" className="h-5 w-5 !text-white" />
        </div>
        <div className="min-w-0">
          <h3 className="!mb-0 text-sm font-extrabold text-[#0A2947]">Bulk review</h3>
          <p className="!mb-0 text-xs text-[#6B7280]">
            {skipped
              ? "Answer the ones you skipped on one page, one after another. Your answer still reaches your clinician."
              : "Answer them all on one page, one after another, instead of opening each one."}
          </p>
        </div>
      </div>
      <Button
        onClick={onOpen}
        className="!h-10 !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-4 !text-sm !font-semibold !text-white hover:!bg-[#E4002B]/60 hover:!text-white"
      >
        {skipped ? `Review all skipped (${count})` : `Review all (${count})`}
      </Button>
    </section>
  );
}

/** IDENTITY_MISMATCH still waiting on the patient (answered or "not sure" aside). */
export function isPendingIdentityCheck(item: ReviewItem): boolean {
  return item.triggerType === "IDENTITY_MISMATCH" && item.awaitingInput;
}

export const IDENTITY_SECTION_COPY = {
  title: "Confirm your uploads",
  /** One line under the group heading; the full explanation is in the detail panel. */
  hint: "Only you can answer these.",
  body:
    "Only you can answer these \u2014 your care team can't see your contact details. " +
    "Records from these uploads are on hold until you answer.",
};

/** A small heading over a group in the Flagged Records list. */
function GroupHeading({ title, count, hint }: { title: string; count: number; hint?: string }) {
  return (
    <div>
      <h3 className="!mb-0 text-sm font-extrabold text-[#0A2947]">
        {title} <span className="font-semibold text-[#9CA3AF]">({count})</span>
      </h3>
      {hint && <p className="!mb-0 !mt-0.5 text-xs text-[#6B7280]">{hint}</p>}
    </div>
  );
}

/**
 * One record in the queue's list — a medicine, a record conflict, or an
 * identity check ("Confirm your uploads"). Identity checks are patient-only:
 * never assigned to a clinician, never skipped, never moved on by themselves.
 * Their records stay on hold until the patient answers, which the row says.
 */
function QueueRow({ row, selected, onSelect }: { row: Row; selected: boolean; onSelect: (id: string) => void }) {
  const { item, med, title, kind } = row;
  const identityPending = isPendingIdentityCheck(item);
  return (
    <Button
      variant="outline"
      data-testid="hrq-row"
      onClick={() => onSelect(item.id)}
      className={cn(
        "!block !h-auto !w-full !whitespace-normal !rounded-xl !px-4 !py-3 !text-left !shadow-sm",
        listCardClass(selected),
      )}
    >
      {!med ? (
        <div className="flex min-w-0 items-center justify-between gap-3">
          <h3 className="!mb-0 min-w-0 break-words text-sm font-bold text-[#0A2947]">{title}</h3>
          <span className="shrink-0 text-xs font-medium text-[#6B7280]">{kind}</span>
        </div>
      ) : (
        <div className="flex min-w-0 items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <h3 className="!mb-0 min-w-0 break-words text-sm font-bold text-[#0A2947]">{med.name}</h3>
            {med.dosage !== "Not specified" && (
              <span className="shrink-0 rounded-md bg-[#E4002B]/70 px-2 py-0.5 text-[10px] font-bold text-white">{med.dosage}</span>
            )}
            <RxcuiTag rxcui={med.rxcui} />
          </div>
          {med.status === "active" ? (
            <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-[#16A34A]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#16A34A]" />
              Active
            </span>
          ) : (
            <span className="shrink-0 text-xs font-medium text-[#9CA3AF]">Past</span>
          )}
        </div>
      )}
      <p className="!mb-0 mt-1 text-xs text-[#6B7280]">{ISSUE_LABELS[item.triggerType]}</p>
      {identityPending && (
        <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#FEF6DC] px-2 py-0.5 text-[11px] font-bold text-[#946200]">
          <Icon icon="lucide:lock" className="h-3 w-3" />
          Records on hold until you answer
        </span>
      )}
      {item.clinicianQuestion && (
        <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#FEF6DC] px-2 py-0.5 text-[11px] font-bold text-[#946200]">
          <Icon icon="lucide:message-circle-question" className="h-3 w-3" />
          Your clinician asked a question
        </span>
      )}
    </Button>
  );
}

/** The fields shown for a record — one list, so a single record and a pair look the same. */
function recordFields(med: Medication): { label: string; value: string }[] {
  return [
    { label: "Medication", value: med.name },
    { label: "Dosage", value: med.dosage },
    { label: "Frequency", value: med.frequency },
    { label: "Route", value: med.route === "oral" ? "By Mouth" : med.route === "other" ? "Not specified" : med.route },
    { label: "Prescribed By", value: med.prescribedBy || "Not recorded" },
    { label: "Start Date", value: formatDate(med.startDate) },
  ];
}

const NOT_GIVEN = new Set(["", "not specified", "not available", "unknown provider"]);
const comparable = (value: string) => value.trim().toLowerCase().replace(/\s+/g, " ");

/** Field labels whose values differ between two records (a value missing on one side isn't a difference). */
function differingFields(a: Medication, b: Medication): Set<string> {
  const other = new Map(recordFields(b).map((f) => [f.label, comparable(f.value)]));
  return new Set(
    recordFields(a)
      .filter((f) => {
        const va = comparable(f.value);
        const vb = other.get(f.label) ?? "";
        return !NOT_GIVEN.has(va) && !NOT_GIVEN.has(vb) && va !== vb;
      })
      .map((f) => f.label),
  );
}

function FieldGrid({ med, columns, differs }: { med: Medication; columns: string; differs?: Set<string> }) {
  return (
    <div className={cn("grid gap-x-4 gap-y-4", columns)}>
      {recordFields(med).map((field) => {
        const isDifferent = differs?.has(field.label) ?? false;
        return (
          <div
            key={field.label}
            data-differs={isDifferent || undefined}
            // A conflicting field is highlighted (same amber as before), so it's the first thing seen.
            className={isDifferent ? "-m-1.5 rounded-lg bg-[#FEF6DC] p-1.5" : undefined}
          >
            <p className={cn("text-xs", isDifferent ? "font-semibold text-[#946200]" : "text-[#9CA3AF]")}>{field.label}</p>
            <p className={cn("mt-0.5 text-sm", isDifferent ? "font-extrabold text-[#0A2947]" : "font-bold text-[#111827]")}>{field.value}</p>
          </div>
        );
      })}
    </div>
  );
}

function PairFields({ meds }: { meds: Medication[] }) {
  const existing = meds.find((m) => m.review?.recordRole === "EXISTING")!;
  const incoming = meds.find((m) => m.review?.recordRole === "INCOMING")!;
  // Same comparison card as a record conflict: names once on the left, a column per record.
  return (
    <ComparisonTable
      label="Your two records"
      rows={recordFields(existing).map((f) => f.label)}
      differs={differingFields(existing, incoming)}
      columns={[
        { title: "Earlier record", med: existing },
        { title: "Newer record", med: incoming },
      ].map(({ title, med }) => ({
        key: med.id,
        testId: "review-record",
        values: Object.fromEntries(recordFields(med).map((f) => [f.label, f.value])),
        header: (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="!mb-0 text-sm font-extrabold text-[#0A2947]">{title}</p>
            <div className="flex items-center gap-1.5">
              <RxcuiTag rxcui={med.rxcui} />
              <SourceBadge vendor={med.sourceVendor} />
            </div>
          </div>
        ),
      }))}
    />
  );
}

function DetailCard(props: {
  row: Row; onClose: () => void; onReview: (id: string) => void; onSkip: (id: string) => void; skipping: boolean;
}) {
  return props.row.med ? <MedicationDetailCard {...props} med={props.row.med} /> : <EmpiDetailCard {...props} />;
}


const PRIMARY_BUTTON =
  "!flex !h-auto !shrink-0 !items-center !gap-1.5 !rounded-lg !bg-[#0A2947] !px-4 !py-2 !text-sm !font-semibold !text-white hover:!bg-[#0A2947] hover:!opacity-90";
const SECONDARY_BUTTON =
  "!h-auto !rounded-lg !border-[#0A2947] !px-4 !py-2 !text-sm !font-semibold !text-[#0A2947] hover:!border-[#0A2947] hover:!bg-[#F9FAFB] hover:!text-[#0A2947]";

/** Flagged: Review (or Reply to a clinician's question), Skip where allowed, and what each does. */
function FlaggedActions({ item, onReview, onSkip, skipping, canSkip }: {
  item: ReviewItem; onReview: (id: string) => void; onSkip: (id: string) => void; skipping: boolean; canSkip: boolean;
}) {
  const identity = item.triggerType === "IDENTITY_MISMATCH";
  const replying = Boolean(item.clinicianQuestion);
  return (
    <div className="mt-5 border-t border-[#E5E7EB] pt-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-[#111827]">{replying ? "Your clinician asked you a question" : "Please review"}</p>
          <p className="text-xs text-[#6B7280]">
            {identity ? IDENTITY_SECTION_COPY.body
              : replying ? "Reply so they can finish reviewing this."
              : "Your clinician makes the final decision either way."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => onReview(item.id)} disabled={skipping} className={PRIMARY_BUTTON}>
            <Icon icon={replying ? "lucide:reply" : "lucide:clipboard-pen"} className="h-4 w-4 !text-white" />
            {replying ? ACTION_LABELS.reply : ACTION_LABELS.review}
          </Button>
          {canSkip && (
            // While skipping: unclickable, spinner centred over the label.
            <Button variant="outline" loading={skipping} onClick={() => onSkip(item.id)} className={SECONDARY_BUTTON}>
              {ACTION_LABELS.skip}
            </Button>
          )}
        </div>
      </div>
      {/* What each choice does, in plain words. */}
      <ul className="!mb-0 !mt-4 grid gap-2 !pl-0 text-xs text-[#4B5563]" aria-label="What happens next">
        <li className="flex items-start gap-2">
          <Icon icon="lucide:clipboard-pen" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#0A2947]" />
          {identity ? (
            <span><span className="font-semibold text-[#0A2947]">{ACTION_LABELS.review}:</span> tell us whether this upload is yours. Only you can answer this.</span>
          ) : replying ? (
            <span><span className="font-semibold text-[#0A2947]">{ACTION_LABELS.reply}:</span> answer your clinician's question. Your reply goes straight to them.</span>
          ) : (
            <span><span className="font-semibold text-[#0A2947]">{ACTION_LABELS.review}:</span> tell us which is right and add a comment. Your answer goes to your clinician with this record.</span>
          )}
        </li>
        {canSkip && (
          <li className="flex items-start gap-2">
            <Icon icon="lucide:skip-forward" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#6B7280]" />
            <span><span className="font-semibold text-[#0A2947]">Skip:</span> it goes to your clinician as it is, without any answer or comment from you.</span>
          </li>
        )}
      </ul>
    </div>
  );
}

/** The button on a Reviewed / Skipped item that can still take an answer, or null. */
export function answeredActionLabel(item: ReviewItem, tab: TabKey): string | null {
  if (!item.canRespond) return null;
  // Settled by the system from an earlier "Yes": the one thing left is to undo it.
  if (autoConfirmedText(item)) return ACTION_LABELS.notYou;
  return tab === "skipped" ? ACTION_LABELS.answer : ACTION_LABELS.update;
}

/** Reviewed / Skipped: what happened, where it stands, and the one button left (if any). */
function AnsweredActions({ item, tab, onReview }: { item: ReviewItem; tab: TabKey; onReview: (id: string) => void }) {
  const last = lastResponse(item);
  const label = answeredActionLabel(item, tab);
  const tag = outcomeTag(item);
  return (
    <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-[#E5E7EB] pt-5">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <p className="text-sm text-[#6B7280]">
          {autoConfirmedText(item) ?? (tab === "reviewed" && last ? answeredText(last.answer) : skippedText(item))}
        </p>
        <span className={cn("shrink-0 rounded-full px-3 py-1 text-xs font-semibold", tag.className)}>{tag.label}</span>
      </div>
      {label && (
        <Button onClick={() => onReview(item.id)} className={PRIMARY_BUTTON}>
          {label}
        </Button>
      )}
    </div>
  );
}

/** IDENTITY_MISMATCH / GOLDEN_RECORD_CONFLICT: what it's about, and Review (opens the answer form). */
function EmpiDetailCard({ row, onClose, onReview, onSkip, skipping }: {
  row: Row; onClose: () => void; onReview: (id: string) => void; onSkip: (id: string) => void; skipping: boolean;
}) {
  const { item, title, kind, tab } = row;
  const identity = item.empi?.identity;
  const conflict = item.empi?.conflict;
  // Skip only where the item allows it (never on an identity check) and only in the patient's window.
  const canSkip = item.allowedAnswers.includes("SKIPPED") && item.itemStatus === "AWAITING_PATIENT";
  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white p-6 shadow-sm" aria-label="Review details">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#F36F8F]">
            <Icon icon={identity ? "lucide:user-search" : "lucide:git-compare"} className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-base font-extrabold text-[#0A2947]">{title}</h2>
            <p className="text-xs text-[#9CA3AF]">{kind} · Flagged {formatDaysAgo(daysSince(item.createdAt))}</p>
          </div>
        </div>
        <Button variant="link" onClick={onClose} aria-label="Close" className="!h-auto !p-0 !text-[#9CA3AF] hover:!text-[#111827]">
          <Icon icon="lucide:x" className="h-4 w-4" />
        </Button>
      </div>

      <div className="mt-5 rounded-xl border border-[#F8C9D2] bg-[#FFF5F7] p-4">
        <p className="text-xs font-extrabold tracking-wide text-[#E4002B]">REASON:</p>
        <p className="mt-1 text-sm font-semibold text-[#0A2947]">{ISSUE_LABELS[item.triggerType]}</p>
        {identity?.matchBand && <p className="mt-0.5 text-sm text-[#4B5563]">{MATCH_BAND_TEXT[identity.matchBand]}</p>}
        {identity && identity.reasons.length > 0 && (
          <p className="mt-0.5 text-sm text-[#4B5563]">{IDENTITY_REASON_TEXT[identity.reasons[0]]}</p>
        )}
        {conflict && (
          <p className="mt-0.5 text-sm text-[#4B5563]" data-testid="conflict-reason">
            {conflictReasonText(conflict.conflictReason, RESOURCE_NOUN[conflict.resourceType])}
          </p>
        )}
      </div>

      {item.outcome && (
        <section aria-label="Clinician's decision" data-testid="empi-outcome" className="mt-5 rounded-xl border border-[#027A48]/25 bg-[#ECFDF3] p-4">
          <p className="text-sm font-extrabold text-[#0A2947]">{outcomeActionLabel(item.outcome.action, false)}</p>
          <p className="mt-1 text-sm text-[#374151]">“{item.outcome.reason}”</p>
        </section>
      )}

      {conflict && (
        <div className="mt-5">
          <ConflictOptions detail={conflict} label="Your records" />
        </div>
      )}

      {tab === "flagged" ? (
        <FlaggedActions item={item} onReview={onReview} onSkip={onSkip} skipping={skipping} canSkip={canSkip} />
      ) : (
        <AnsweredActions item={item} tab={tab} onReview={onReview} />
      )}
    </div>
  );
}

function MedicationDetailCard({ row, med, onClose, onReview, onSkip, skipping }: {
  row: Row; med: Medication; onClose: () => void; onReview: (id: string) => void; onSkip: (id: string) => void; skipping: boolean;
}) {
  const { item, meds, tab } = row;
  const isPair = isPairItem(meds);

  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white p-6 shadow-sm" aria-label="Review details">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#F36F8F]">
            <Icon icon="lucide:pill" className="h-5 w-5 text-white" />
          </div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-extrabold text-[#0A2947]">{med.name}</h2>
            {med.status === "active" && (
              <span className="rounded-md bg-[#E4002B]/70 px-2 py-0.5 text-[10px] font-bold text-white">Active</span>
            )}
            {!isPair && <RxcuiTag rxcui={med.rxcui} />}
          </div>
        </div>
        <Button variant="link" onClick={onClose} aria-label="Close" className="!h-auto !p-0 !text-[#9CA3AF] hover:!text-[#111827]">
          <Icon icon="lucide:x" className="h-4 w-4" />
        </Button>
      </div>
      <p className="mt-1 pl-14 text-xs text-[#9CA3AF]">Flagged {formatDaysAgo(daysSince(item.createdAt))}</p>

      <div className="mt-5 rounded-xl border border-[#F8C9D2] bg-[#FFF5F7] p-4">
        <p className="text-xs font-extrabold tracking-wide text-[#E4002B]">REASON:</p>
        <p className="mt-1 text-sm font-semibold text-[#0A2947]">{reviewTitle(meds)}</p>
        {reviewTitle(meds) !== ISSUE_LABELS[item.triggerType] && (
          <p className="mt-0.5 text-sm text-[#4B5563]">{ISSUE_LABELS[item.triggerType]}</p>
        )}
      </div>

      <div className="mt-5 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-4">
        <h3 className="mb-4 text-sm font-extrabold text-[#0A2947]">Extracted Information</h3>
        {isPair ? (
          // Two records that disagree: the same field grid for each, side by
          // side, with the fields that differ marked.
          <PairFields meds={meds} />
        ) : (
          <FieldGrid med={med} columns="grid-cols-2 sm:grid-cols-3" />
        )}
      </div>

      {/* The conversation sits right above Reply / Review, after the records it's about. */}
      {((item.clinicianQuestions ?? []).length > 0 || item.clinicianQuestion) && (
        <PatientConversation
          className="mt-5"
          messages={itemConversation(item, isPair)}
          viewer="patient"
          maxVisible={3}
          waitingFor={item.itemStatus === "NEEDS_MORE_INFO" ? "patient" : null}
        />
      )}

      {tab === "flagged" ? (
        <FlaggedActions item={item} onReview={onReview} onSkip={onSkip} skipping={skipping} canSkip />
      ) : (
        <AnsweredActions item={item} tab={tab} onReview={onReview} />
      )}
    </div>
  );
}
